import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { InquiryForm } from "@/components/site/InquiryForm";
import { Bed, Bathtub, MapPin, Calendar, Lightning, ArrowLeft, CheckCircle, House } from "@phosphor-icons/react";

export default function PropertyDetailPage() {
  const { slug } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/properties/${slug}`);
        setProperty(data);
      } catch {
        setProperty(null);
      }
      setLoading(false);
    })();
  }, [slug]);

  if (loading) {
    return (
      <div className="App">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
          <div className="h-[60vh] bg-[var(--bg-secondary)] animate-pulse" />
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="App">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 text-center">
          <h1 className="h1 text-5xl">Property not found</h1>
          <Link to="/properties" className="btn-outline mt-8 inline-flex">
            <ArrowLeft size={18} /> Back to properties
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const images = property.images?.length ? property.images : [property.hero_image];
  const hero = images[0];
  const supports = images.slice(1, 4);

  return (
    <div className="App">
      <Navbar />

      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-8">
        <Link to="/properties" className="text-sm text-[var(--text-tertiary)] hover:text-[var(--text-primary)] inline-flex items-center gap-1" data-testid="back-to-properties">
          <ArrowLeft size={14} /> All properties
        </Link>
      </div>

      {/* Gallery */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-6" data-testid="property-gallery">
        {property.coming_soon ? (
          <div className="w-full h-[60vh] md:h-[70vh] flex items-center justify-center bg-[var(--brand)] text-white">
            <div className="text-center px-6">
              <div className="label-uppercase !text-[var(--accent)]">Watch this space</div>
              <div className="font-display text-6xl md:text-8xl tracking-tighter mt-4">Coming Soon</div>
              <div className="mt-4 text-white/70 max-w-md mx-auto">
                Our third Hull rental is being added to the portfolio. Photography, pricing and viewing slots will land here shortly.
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-12 gap-3" style={{ maxHeight: "80vh" }}>
            <div className="col-span-12 md:col-span-8 aspect-[16/10] md:aspect-auto md:h-[70vh] overflow-hidden">
              <img src={hero} alt={property.name} className="w-full h-full object-cover" />
            </div>
            <div className="col-span-12 md:col-span-4 grid grid-cols-3 md:grid-cols-1 gap-3">
              {supports.length > 0 ? supports.map((src, i) => (
                <div key={i} className="aspect-[4/3] md:aspect-auto md:h-[calc((70vh-1.5rem)/2)] overflow-hidden">
                  <img src={src} alt={`${property.name} ${i + 1}`} className="w-full h-full object-cover" />
                </div>
              )) : (
                <div className="aspect-[4/3] bg-[var(--bg-secondary)]" />
              )}
            </div>
          </div>
        )}
      </section>

      {/* Header */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-12 border-b border-[var(--border-subtle)]">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <div className="flex items-center gap-3">
              {property.coming_soon ? (
                <span className="tag-let" style={{ background: 'var(--brand)' }} data-testid="property-detail-status">Coming Soon</span>
              ) : (
                <span className={property.available ? "tag-available" : "tag-let"} data-testid="property-detail-status">
                  {property.available ? "Available" : "Let Agreed"}
                </span>
              )}
              <span className="label-uppercase">{property.target}</span>
            </div>
            <h1 className="h1 text-5xl md:text-6xl mt-4" data-testid="property-detail-name">{property.name}</h1>
            <div className="mt-3 flex items-center gap-2 text-[var(--text-secondary)]">
              <MapPin size={16} /> {property.address}{property.postcode ? `, Hull · ${property.postcode}` : ', Hull'}
            </div>          </div>
          <div className="text-right">
            {property.coming_soon ? (
              <>
                <div className="font-display font-semibold text-4xl md:text-5xl tracking-tight text-[var(--text-tertiary)]">TBC</div>
                <div className="label-uppercase mt-1">Pricing soon</div>
              </>
            ) : property.price_pppw ? (
              <>
                <div className="font-display font-semibold text-5xl tracking-tight text-[var(--brand)]" data-testid="property-detail-price">
                  £{property.price_pppw} <span className="text-xl font-medium text-[var(--text-secondary)]">pppw</span>
                </div>
                <div className="label-uppercase mt-1">bills inclusive · approx £{property.price_pcm.toLocaleString()} pcm</div>
              </>
            ) : (
              <>
                <div className="font-display font-semibold text-5xl tracking-tight text-[var(--brand)]" data-testid="property-detail-price">
                  £{property.price_pcm.toLocaleString()} <span className="text-xl font-medium text-[var(--text-secondary)]">pcm</span>
                </div>
                <div className="label-uppercase mt-1">per calendar month</div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Content split */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
            <Stat icon={<Bed size={20} />} label="Bedrooms" value={property.bedrooms} />
            <Stat icon={<Bathtub size={20} />} label="Bathrooms" value={property.bathrooms} />
            <Stat icon={<Calendar size={20} />} label="Available" value={new Date(property.available_from).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })} />
            <Stat icon={<Lightning size={20} />} label="Bills" value={property.bills_included ? "Included" : "Separate"} />
          </div>

          <div className="label-uppercase">About this property</div>
          <h2 className="h2 text-3xl md:text-4xl mt-3">{property.tagline}</h2>
          <p className="mt-6 text-lg text-[var(--text-secondary)] leading-relaxed">{property.description}</p>

          <div className="mt-12">
            <div className="label-uppercase">What's included</div>
            <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {property.amenities?.map((a) => (
                <li key={a} className="flex items-center gap-2 text-[var(--text-primary)]">
                  <CheckCircle size={18} weight="fill" className="text-[var(--brand)] shrink-0" />
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-6 border-t border-[var(--border-subtle)] pt-8">
            <KV k="Furnished" v={property.furnished ? "Yes" : "Unfurnished"} />
            <KV k="Bills" v={property.bills_included ? "Included" : "Tenant pays"} />
            <KV k="Address" v={`${property.address}, Hull`} />
            <KV k="Postcode" v={property.postcode} />
            <KV k="Tenant type" v={property.target} />
            <KV k="Available from" v={new Date(property.available_from).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })} />
          </div>
        </div>

        <aside className="lg:col-span-5">
          <div className="lg:sticky lg:top-24 bg-white border border-[var(--border-subtle)] p-8" data-testid="property-inquiry-sidebar">
            <InquiryForm property={property} />
          </div>
        </aside>
      </section>

      <Footer />
    </div>
  );
}

const Stat = ({ icon, label, value }) => (
  <div className="border border-[var(--border-subtle)] p-4">
    <div className="text-[var(--text-tertiary)]">{icon}</div>
    <div className="label-uppercase mt-3">{label}</div>
    <div className="mt-1 font-display font-medium text-lg">{value}</div>
  </div>
);

const KV = ({ k, v }) => (
  <div>
    <div className="label-uppercase">{k}</div>
    <div className="mt-1 text-[var(--text-primary)]">{v}</div>
  </div>
);
